import { worldMeta, worldOf, navGoals, GEM_SPRITE } from "./navShared";
import { useContent } from "../../context/ContentContext";
import { mergeJuniorHow, fillJuniorHow, renderBold } from "../../lib/juniorHow";

// Which optional features a grid actually uses - drives the legend and the instructions
export const navFeatures = (nav) => {
  const exCats = new Set(nav?.excluded_categories || []);
  const exBlocks = new Set(nav?.excluded_blocks || []);
  const on = (cat, ...types) => !exCats.has(cat) && types.some((t) => !exBlocks.has(t));
  const world = worldOf(nav);
  return {
    world,
    goals: navGoals(nav).length,
    fuel: world === "moon" ? (nav?.fuel ?? 0) : null,
    fuelCells: world === "moon" && (nav?.fuel_cells || []).length > 0,
    rocks: (nav?.rocks || []).length > 0,
    dangers: (nav?.dangers || []).length > 0,
    tunnels: (nav?.tunnels || []).length > 0,
    powers: (nav?.powers || []).length > 0,
    backward: on("move", "nav_backward"),
    loops: on("loops", "controls_repeat_ext", "nav_repeat_until_treasure"),
    sensors: on("logic", "nav_sensor") && on("logic", "controls_if"),
    variables: on("variables", "variables_set", "math_change", "variables_get"),
    functions: on("functions", "procedures_defnoreturn"),
    limit: nav?.max_blocks || null,
  };
};

export const navLimitLabel = (nav) => (nav?.max_blocks ? `Max ${nav.max_blocks} blocks` : "Unlimited blocks");

const Legend = ({ src, label, rounded, testId }) => (
  <span className="inline-flex items-center gap-1.5" data-testid={testId}><img src={src} alt="" className={`w-5 h-5 ${rounded ? "rounded-sm" : ""}`} /> {label}</span>
);

const B = ({ children }) => <span className="font-semibold text-foreground">{children}</span>;

// Every line the grid needs, in this order; the key doubles as the content field and the testid suffix
const LINES = [
  ["play", () => true], ["move", () => true], ["collect", (f) => f.goals > 1], ["fuel", (f) => f.fuel != null], ["fuel_cells", (f) => f.fuelCells],
  ["backward", (f) => f.backward], ["rocks", (f) => f.rocks], ["danger", (f) => f.dangers && f.world !== "moon", "dangers"], ["cracks", (f) => f.dangers && f.world === "moon"],
  ["tunnel", (f) => f.tunnels, "tunnels"], ["power", (f) => f.powers], ["loops", (f) => f.loops], ["scanner", (f) => f.loops || f.sensors], ["sensors", (f) => f.sensors], ["variables", (f) => f.variables],
  ["functions", (f) => f.functions], ["limit", () => true], ["submit", () => true],
];

// Only the rules that matter for this grid. `text` lets the admin editor preview unsaved copy.
export const NavHowItWorks = ({ nav, text }) => {
  const { content } = useContent();
  const t = text || mergeJuniorHow(content);
  const f = navFeatures(nav);
  const w = worldMeta(nav);
  const vars = { ...w.labels, n: f.goals, fuel: f.fuel, limit: f.limit };
  const line = (key, field) => fillJuniorHow(key === "limit" ? (f.limit ? t.limit : t.no_limit) : t[field || key], vars);
  const mines = f.world === "mines";
  const legend = {
    vehicle: mines ? t.legend_drill : `your ${w.labels.vehicle}`,
    goal: mines ? t.legend_treasure : `${w.labels.goal} (collect ${f.goals > 1 ? `all ${f.goals}` : "it"})`,
    obstacle: mines ? t.legend_rock : w.labels.obstacle,
    danger: mines ? t.legend_danger : w.labels.danger,
  };
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 text-sm" data-testid="nav-how-it-works" data-world={f.world}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{t.heading}{!mines && <span className="ml-2 text-xs font-medium text-muted-foreground" data-testid="nav-how-world">{w.name} world</span>}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid="nav-legend">
          <Legend src={w.sprites.vehicle} label={legend.vehicle} testId="nav-legend-vehicle" />
          <Legend src={w.sprites.goal} label={legend.goal} testId="nav-legend-goal" />
          {f.rocks && <Legend src={w.sprites.obstacle} label={legend.obstacle} rounded testId="nav-legend-obstacle" />}
          {f.dangers && <Legend src={w.sprites.danger} label={legend.danger} rounded={f.world === "moon"} testId="nav-legend-danger" />}
          {f.tunnels && <Legend src={w.sprites.tunnel} label={t.legend_tunnel} testId="nav-legend-tunnel" />}
          {f.fuelCells && <Legend src={w.sprites.fuel} label={t.legend_fuel} testId="nav-legend-fuel" />}
          {f.powers && <Legend src={w.sprites.power} label={t.legend_power} testId="nav-legend-power" />}
          {(f.loops || f.sensors) && <Legend src={GEM_SPRITE} label={t.legend_gem} testId="nav-legend-gem" />}
        </div>
      </div>
      <ul className="list-disc pl-5 space-y-1 text-muted-foreground" data-testid="nav-instructions-list">
        {LINES.filter(([, when]) => when(f)).map(([key, , field]) => (
          <li key={key} data-testid={`nav-instr-${key}`}>{renderBold(line(key, field), B)}</li>
        ))}
      </ul>
    </div>
  );
};
